import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import { toast } from 'react-toastify';
import supplierService, { Supplier, SupplierType, SUPPLIER_TYPE_LABELS } from '../../services/supplierService';
import { toPersianDigitsStr } from '../../utils/persianNumber';
import SupplierDialog from './SupplierDialog';
import { faNum, faRial, faToman } from './format';

interface Props {
  canWrite: boolean;
  /** Jump to the invoices tab filtered by this supplier */
  onShowInvoices: (supplierId: string) => void;
  /** Bumped by the parent after an invoice is created so aggregates refresh */
  reloadKey?: number;
}

const SuppliersTab: React.FC<Props> = ({ canWrite, onShowInvoices, reloadKey }) => {
  const [rows, setRows] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [type, setType] = useState<SupplierType | ''>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Supplier | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await supplierService.listSuppliers({ limit: 200, search: search.trim() || undefined, type });
      setRows(res.items);
    } catch (e: any) {
      toast.error(e?.message || 'خطا در دریافت تأمین‌کنندگان');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [search, type]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load, reloadKey]);

  const totals = useMemo(
    () =>
      rows.reduce(
        (a, r) => ({
          invoices: a.invoices + (r.invoiceCount || 0),
          qty: a.qty + (r.totalQuantity || 0),
          cost: a.cost + (r.totalCostToman || 0),
          paid: a.paid + (r.totalPaidRial || 0),
        }),
        { invoices: 0, qty: 0, cost: 0, paid: 0 },
      ),
    [rows],
  );

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (s: Supplier) => {
    setEditing(s);
    setDialogOpen(true);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
        <TextField
          size="small"
          placeholder="جستجوی نام، تلفن، شناسه…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          label="نوع"
          value={type}
          onChange={(e) => setType(e.target.value as SupplierType | '')}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">همه</MenuItem>
          {(Object.keys(SUPPLIER_TYPE_LABELS) as SupplierType[]).map((t) => (
            <MenuItem key={t} value={t}>
              {SUPPLIER_TYPE_LABELS[t]}
            </MenuItem>
          ))}
        </TextField>
        <Tooltip title="بارگذاری مجدد">
          <IconButton onClick={load} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        {canWrite && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            افزودن تأمین‌کننده
          </Button>
        )}
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>نام</TableCell>
              <TableCell>نوع</TableCell>
              <TableCell>تماس</TableCell>
              <TableCell align="center">تعداد فاکتور</TableCell>
              <TableCell align="center">تعداد اقلام</TableCell>
              <TableCell>جمع بهای تمام‌شده</TableCell>
              <TableCell>جمع پرداختی</TableCell>
              <TableCell align="center">وضعیت</TableCell>
              <TableCell align="center">عملیات</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">تأمین‌کننده‌ای ثبت نشده است</Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((s) => (
                <TableRow key={s._id} hover>
                  <TableCell>
                    <Typography fontWeight={600}>{s.name}</Typography>
                    {s.economicCode && (
                      <Typography variant="caption" color="text.secondary">
                        کد اقتصادی: {toPersianDigitsStr(s.economicCode)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{SUPPLIER_TYPE_LABELS[s.type] || s.type || '—'}</TableCell>
                  <TableCell>
                    {s.phone ? <span dir="ltr">{toPersianDigitsStr(s.phone)}</span> : '—'}
                    {s.email && (
                      <Typography variant="caption" display="block" color="text.secondary" dir="ltr">
                        {s.email}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">{faNum(s.invoiceCount || 0)}</TableCell>
                  <TableCell align="center">{faNum(s.totalQuantity || 0)}</TableCell>
                  <TableCell>{faToman(s.totalCostToman || 0)}</TableCell>
                  <TableCell>{faRial(s.totalPaidRial || 0)}</TableCell>
                  <TableCell align="center">
                    <Chip
                      size="small"
                      label={s.isActive !== false ? 'فعال' : 'غیرفعال'}
                      color={s.isActive !== false ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="فاکتورهای خرید">
                      <IconButton size="small" onClick={() => onShowInvoices(s._id)}>
                        <ReceiptLongIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {canWrite && (
                      <Tooltip title="ویرایش">
                        <IconButton size="small" onClick={() => openEdit(s)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
            {rows.length > 1 && (
              <TableRow sx={{ '& td': { fontWeight: 700, bgcolor: 'action.hover' } }}>
                <TableCell colSpan={3}>جمع ({faNum(rows.length)} تأمین‌کننده)</TableCell>
                <TableCell align="center">{faNum(totals.invoices)}</TableCell>
                <TableCell align="center">{faNum(totals.qty)}</TableCell>
                <TableCell>{faToman(totals.cost)}</TableCell>
                <TableCell>{faRial(totals.paid)}</TableCell>
                <TableCell colSpan={2} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <SupplierDialog
        open={dialogOpen}
        supplier={editing}
        onClose={() => setDialogOpen(false)}
        onSaved={() => {
          setDialogOpen(false);
          load();
        }}
      />
    </Box>
  );
};

export default SuppliersTab;
